import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Clock, Loader2, Edit, Eye } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_HOURS = [
  { day: 'Lunes', morning_open: '09:00', morning_close: '13:00', afternoon_open: '15:00', afternoon_close: '19:00', is_open: true },
  { day: 'Martes', morning_open: '09:00', morning_close: '13:00', afternoon_open: '15:00', afternoon_close: '19:00', is_open: true },
  { day: 'Miércoles', morning_open: '09:00', morning_close: '13:00', afternoon_open: '15:00', afternoon_close: '19:00', is_open: true },
  { day: 'Jueves', morning_open: '09:00', morning_close: '13:00', afternoon_open: '15:00', afternoon_close: '19:00', is_open: true },
  { day: 'Viernes', morning_open: '09:00', morning_close: '13:00', afternoon_open: '15:00', afternoon_close: '19:00', is_open: true },
  { day: 'Sábado', morning_open: '09:00', morning_close: '13:00', afternoon_open: '', afternoon_close: '', is_open: true },
  { day: 'Domingo', morning_open: '', morning_close: '', afternoon_open: '', afternoon_close: '', is_open: false }
];

export const BusinessHoursTab = () => {
  const { toast } = useToast();
  const [businessHours, setBusinessHours] = useState(DEFAULT_HOURS);
  const [hoursLoading, setHoursLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const token = localStorage.getItem('krolbolt_token');

  useEffect(() => {
    fetchBusinessHours();
  }, []);

  const fetchBusinessHours = async () => {
    setHoursLoading(true);
    try {
      const response = await fetch(`${API}/business-hours/`);
      if (response.ok) {
        const data = await response.json();
        if (data.hours && data.hours.length > 0) {
          setBusinessHours(data.hours);
        }
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setHoursLoading(false);
    }
  };

  const updateBusinessHours = async () => {
    setHoursLoading(true);
    try {
      const response = await fetch(`${API}/business-hours/`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ hours: businessHours })
      });

      const data = await response.json();

      if (response.ok) {
        toast({ title: "Éxito", description: "Horarios actualizados correctamente" });
        setIsEditing(false);
        fetchBusinessHours();
      } else {
        toast({ title: "Error", description: data.detail || "No se pudieron actualizar los horarios", variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Error", description: "Error al actualizar horarios: " + error.message, variant: "destructive" });
    } finally {
      setHoursLoading(false);
    }
  };

  const updateHour = (index, field, value) => {
    const updated = [...businessHours];
    updated[index] = { ...updated[index], [field]: value };
    setBusinessHours(updated);
  };

  if (hoursLoading && businessHours.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-cyan-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white flex items-center gap-2">
          <Clock className="h-6 w-6" />
          Horarios de Atención
        </h2>
        {!isEditing ? (
          <Button 
            onClick={() => setIsEditing(true)}
            className="bg-gradient-to-r from-cyan-600 to-emerald-600"
          >
            <Edit className="mr-2 h-4 w-4" />
            Editar Horarios
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button 
              variant="outline"
              onClick={() => {
                setIsEditing(false);
                fetchBusinessHours();
              }}
              disabled={hoursLoading}
              className="border-slate-600 text-slate-300"
            >
              Cancelar
            </Button>
            <Button 
              onClick={updateBusinessHours}
              disabled={hoursLoading}
              className="bg-gradient-to-r from-cyan-600 to-emerald-600"
            >
              {hoursLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Guardar Cambios
            </Button>
          </div>
        )}
      </div>

      {!isEditing ? (
        <Card className="bg-slate-800/50 backdrop-blur-sm border-slate-700">
          <CardContent className="p-6 space-y-3">
            {businessHours.map((hour, index) => (
              <div key={index} className="flex items-center justify-between p-4 bg-slate-900/50 rounded-lg border border-slate-700">
                <div className="flex items-center gap-4">
                  <span className="text-white font-medium w-28">{hour.day}</span>
                  <Badge variant={hour.is_open ? "default" : "secondary"} className={hour.is_open ? "bg-green-600" : ""}>
                    {hour.is_open ? 'Abierto' : 'Cerrado'}
                  </Badge>
                </div>
                {hour.is_open && (
                  <div className="flex items-center gap-6 text-slate-300 text-sm">
                    {hour.morning_open && hour.morning_close && (
                      <div className="flex items-center gap-2">
                        <span className="text-slate-500">Mañana:</span>
                        <span className="font-mono">{hour.morning_open} - {hour.morning_close}</span>
                      </div>
                    )}
                    {hour.afternoon_open && hour.afternoon_close && (
                      <div className="flex items-center gap-2">
                        <span className="text-slate-500">Tarde:</span>
                        <span className="font-mono">{hour.afternoon_open} - {hour.afternoon_close}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-slate-800/50 backdrop-blur-sm border-slate-700">
          <CardContent className="p-6 space-y-4">
            <div className="grid grid-cols-1 gap-4">
              {businessHours.map((hour, index) => (
                <div key={index} className="p-4 bg-slate-900/50 rounded-lg border border-slate-700 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-white font-medium text-lg">{hour.day}</span>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={hour.is_open}
                        onChange={(e) => updateHour(index, 'is_open', e.target.checked)}
                        className="w-4 h-4"
                      />
                      <span className="text-slate-400 text-sm">Abierto</span>
                    </div>
                  </div>

                  {hour.is_open && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                      <div className="space-y-2">
                        <Label className="text-cyan-400 font-medium">🌅 Mañana</Label>
                        <div className="flex items-center gap-2">
                          <div className="flex-1">
                            <Label className="text-slate-400 text-xs">Apertura</Label>
                            <Input
                              type="time"
                              value={hour.morning_open}
                              onChange={(e) => updateHour(index, 'morning_open', e.target.value)}
                              className="bg-slate-900/50 border-slate-700 text-white mt-1"
                            />
                          </div>
                          <div className="flex-1">
                            <Label className="text-slate-400 text-xs">Cierre</Label>
                            <Input
                              type="time"
                              value={hour.morning_close}
                              onChange={(e) => updateHour(index, 'morning_close', e.target.value)}
                              className="bg-slate-900/50 border-slate-700 text-white mt-1"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-orange-400 font-medium">🌆 Tarde</Label>
                        <div className="flex items-center gap-2">
                          <div className="flex-1">
                            <Label className="text-slate-400 text-xs">Apertura</Label>
                            <Input
                              type="time"
                              value={hour.afternoon_open}
                              onChange={(e) => updateHour(index, 'afternoon_open', e.target.value)}
                              className="bg-slate-900/50 border-slate-700 text-white mt-1"
                            />
                          </div>
                          <div className="flex-1">
                            <Label className="text-slate-400 text-xs">Cierre</Label>
                            <Input
                              type="time"
                              value={hour.afternoon_close}
                              onChange={(e) => updateHour(index, 'afternoon_close', e.target.value)}
                              className="bg-slate-900/50 border-slate-700 text-white mt-1"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default BusinessHoursTab;
